import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import { FaBook, FaSearch, FaFileInvoice, FaMoneyCheck, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { supplierActions } from '../../sagas/supplierSlice';
import invoiceSupplierService from '../../services/invoiceSupplierService';
import * as reglementFournisseurService from '../../services/reglementFournisseurService';
import { formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const pad = (n) => String(n).padStart(2, '0');
const formatDateInput = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateFr = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString('fr-FR') : '-';
};

const formatMonthKey = (value) => {
  const date = parseDate(value);
  if (!date) return 'Sans date';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey || monthKey === 'Sans date') return 'Sans date';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
};

const getMonthRange = (monthValue) => {
  if (!monthValue) {
    return { dateFrom: '', dateTo: '' };
  }

  const [year, month] = monthValue.split('-').map(Number);
  if (!year || !month) {
    return { dateFrom: '', dateTo: '' };
  }

  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);

  return {
    dateFrom: formatDateInput(startDate),
    dateTo: formatDateInput(endDate),
  };
};

const normalizeNumber = (value) => Number(value || 0);
const extractList = (response) => {
  const candidates = [
    response,
    response?.body,
    response?.content,
    response?.data,
    response?.data?.body,
    response?.data?.content,
    response?.body?.content,
    response?.data?.body?.content,
  ];
  return candidates.find(Array.isArray) || [];
};
const splitDocumentNumbers = (value) => (value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const GrandLivreFournisseurPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const suppliers = useSelector((state) => state.suppliers.suppliers || []);
  const { company } = useSelector((state) => state.auth);

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

  const [filters, setFilters] = useState({
    fournisseurId: null,
    month: `${today.getFullYear()}-${pad(today.getMonth() + 1)}`,
    dateFrom: formatDateInput(startOfMonth),
    dateTo: formatDateInput(tomorrow),
    query: '',
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!suppliers.length) {
      dispatch(supplierActions.search({ query: '' }));
    }
  }, [dispatch, suppliers.length]);

  const supplierOptions = useMemo(() => {
    const mapped = suppliers.map((supplier) => ({
      value: supplier.id,
      label: supplier.typeFournisseur === 'PARTICULIER'
        ? `${supplier.nom || ''} ${supplier.prenom || ''}`.trim()
        : (supplier.entreprise || supplier.nom || '-'),
    }));

    return [
      { value: null, label: t('common.all', 'Tous') },
      ...mapped,
    ];
  }, [suppliers, t]);

  const fetchLedger = async () => {
    setLoading(true);
    setError(null);

    try {
      const [invoiceResult, reglementResult] = await Promise.all([
        invoiceSupplierService.search({
          companyId: company?.id,
          fournisseurId: filters.fournisseurId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
        }),
        reglementFournisseurService.rechercherReglements({
          companyId: company?.id,
          fournisseurId: filters.fournisseurId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
        }),
      ]);

      const invoiceRows = extractList(invoiceResult)
        .filter(invoice => !['BROUILLON', 'ANNULEE'].includes(invoice.statut))
        .map((invoice) => ({
        id: `invoice-${invoice.id}`,
        date: invoice.dateFacture,
        type: 'FACTURE',
        icon: FaFileInvoice,
        numero: invoice.numeroFactfournisseur && invoice.numero
          ? `${invoice.numeroFactfournisseur} / ${invoice.numero}`
          : invoice.numeroFactfournisseur || invoice.numero || '-',
        libelle: invoice.fournisseurNom || [
          t('nav.invoice', 'Facture'),
          splitDocumentNumbers(invoice.paymentsRefs).length
            ? `Paiements: ${splitDocumentNumbers(invoice.paymentsRefs).join(', ')}`
            : null,
        ].filter(Boolean).join(' - '),
        debit: 0,
        credit: normalizeNumber(invoice.montantTTC),
        raw: invoice,
      }));

      const reglementRows = extractList(reglementResult)
        .filter(reglement => ['DECAISSE', 'ENCAISSE'].includes(reglement.statut))
        .map((reglement) => ({
        id: `reglement-${reglement.id}`,
        date: reglement.dateReglement,
        type: 'REGLEMENT',
        icon: FaMoneyCheck,
        numero: reglement.reference || reglement.numeroPiece || reglement.id,
        libelle: reglement.fournisseurNom || [
          getModeLabel(reglement.modeReglement),
          splitDocumentNumbers(reglement.numFactures).length
            ? `Factures: ${splitDocumentNumbers(reglement.numFactures).join(', ')}`
            : null,
          reglement.modeReglement === 'CHEQUE'
            ? `N° ${reglement.numeroCheque || reglement.numeroPiece || ''}`.trim()
            : null,
          reglement.dateEcheance
            ? `Ech. ${formatDateFr(reglement.dateEcheance)}`
            : null,
        ].filter(Boolean).join(' - '),
        debit: normalizeNumber(reglement.montantRegle),
        credit: 0,
        raw: reglement,
      }));

      let runningBalance = 0;
      const merged = [...invoiceRows, ...reglementRows]
        .sort((a, b) => {
          const aDate = parseDate(a.date)?.getTime() || 0;
          const bDate = parseDate(b.date)?.getTime() || 0;
          if (aDate !== bDate) return aDate - bDate;
          return String(a.numero).localeCompare(String(b.numero));
        })
        .map((row) => {
          runningBalance += row.credit - row.debit;
          return {
            ...row,
            balance: runningBalance,
          };
        });

      setRows(merged);
    } catch (err) {
      console.error('Error loading supplier ledger:', err);
      setError(err?.message || t('common.error', 'Une erreur est survenue'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const totalDebit = rows.reduce((sum, row) => sum + row.debit, 0);
    const totalCredit = rows.reduce((sum, row) => sum + row.credit, 0);
    const balance = rows.length ? rows[rows.length - 1].balance : 0;

    return { totalDebit, totalCredit, balance };
  }, [rows]);

  const monthlySections = useMemo(() => {
    const grouped = new Map();

    rows.forEach((row) => {
      const monthKey = formatMonthKey(row.date);
      const existing = grouped.get(monthKey) || {
        monthKey,
        monthLabel: formatMonthLabel(monthKey),
        openingBalance: row.balance - row.credit + row.debit,
        rows: [],
        totalDebit: 0,
        totalCredit: 0,
        closingBalance: row.balance,
      };

      existing.rows.push(row);
      existing.totalDebit += row.debit;
      existing.totalCredit += row.credit;
      existing.closingBalance = row.balance;
      grouped.set(monthKey, existing);
    });

    return Array.from(grouped.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [rows]);

  function getModeLabel(mode) {
    switch (mode) {
      case 'ESPECES':
        return t('facture.modeReglement.espece', 'Especes');
      case 'CHEQUE':
        return t('facture.modeReglement.cheque', 'Cheque');
      case 'VIREMENT':
        return t('facture.modeReglement.virement', 'Virement');
      case 'TRAITE':
        return t('facture.modeReglement.traite', 'Traite');
      case 'CARTE_BANCAIRE':
        return t('facture.modeReglement.card', 'Carte bancaire');
      default:
        return mode || '-';
    }
  }

  const typeLabel = (type) => {
    switch (type) {
      case 'FACTURE':
        return t('nav.invoice', 'Facture fournisseur');
      case 'REGLEMENT':
        return t('nav.listeReglementsFournisseur', 'Reglement fournisseur');
      case 'AVOIR':
        return t('summary.totalAvoirAmount', 'Avoir fournisseur');
      default:
        return type;
    }
  };

  const selectedSupplierLabel = useMemo(() => {
    if (!filters.fournisseurId) return t('common.all', 'Tous');
    return supplierOptions.find((option) => option.value === filters.fournisseurId)?.label || '-';
  }, [filters.fournisseurId, supplierOptions, t]);

  const handleExportExcel = () => {
    if (!rows.length) return;

    const data = rows.map((row) => ({
      Date: formatDateFr(row.date),
      Type: typeLabel(row.type),
      Reference: row.numero,
      Libelle: row.libelle,
      Debit: row.debit || 0,
      Credit: row.credit || 0,
      Solde: row.balance || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 14 },
      { wch: 18 },
      { wch: 20 },
      { wch: 36 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GrandLivreFournisseurs');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(blob, `Grand_Livre_Fournisseurs_${filters.dateFrom}_${filters.dateTo}.xlsx`);
  };

  const handleExportPdf = () => {
    if (!rows.length) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(t('purchases.grandLivre', 'Grand livre fournisseurs'), margin, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(
      `${t('reglement.fournisseur', 'Fournisseur')}: ${selectedSupplierLabel}  |  ${t('common.from', 'Du')} ${filters.dateFrom} ${t('common.to', 'Au')} ${filters.dateTo}`,
      margin,
      26
    );

    if (company?.name) {
      doc.text(company.name, pageWidth - margin, 18, { align: 'right' });
    }

    autoTable(doc, {
      startY: 32,
      head: [[
        t('common.date', 'Date'),
        t('common.type', 'Type'),
        t('common.reference', 'Reference'),
        t('common.description', 'Libelle'),
        t('common.debit', 'Debit'),
        t('common.credit', 'Credit'),
        t('common.balanceAfter', 'Solde'),
      ]],
      body: rows.map((row) => [
        formatDateFr(row.date),
        typeLabel(row.type),
        row.numero,
        row.libelle,
        row.debit ? formatPrice(row.debit, false) : '-',
        row.credit ? formatPrice(row.credit, false) : '-',
        formatPrice(row.balance, false),
      ]),
      foot: [[
        '',
        '',
        '',
        t('common.totals', 'Totaux'),
        formatPrice(totals.totalDebit, false),
        formatPrice(totals.totalCredit, false),
        formatPrice(totals.balance, false),
      ]],
      styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: [233, 242, 255], textColor: [15, 23, 42], fontStyle: 'bold' },
      footStyles: { fillColor: [245, 245, 245], textColor: [15, 23, 42], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 30 },
        2: { cellWidth: 34 },
        3: { cellWidth: 90 },
        4: { cellWidth: 26, halign: 'right' },
        5: { cellWidth: 26, halign: 'right' },
        6: { cellWidth: 26, halign: 'right' },
      },
      margin: { left: margin, right: margin, bottom: 16 },
    });

    doc.save(`Grand_Livre_Fournisseurs_${filters.dateFrom}_${filters.dateTo}.pdf`);
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaBook className="text-primary me-2" />
                {t('purchases.grandLivre', 'Grand livre fournisseurs')}
              </h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm mb-4">
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('reglement.fournisseur', 'Fournisseur')}</Label>
                <Select
                  options={supplierOptions}
                  value={supplierOptions.find((option) => option.value === filters.fournisseurId)}
                  onChange={(option) => setFilters((prev) => ({ ...prev, fournisseurId: option?.value ?? null }))}
                  isSearchable
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.month', 'Mois')}</Label>
                <Input
                  type="month"
                  value={filters.month}
                  onChange={(e) => {
                    const month = e.target.value;
                    const range = getMonthRange(month);
                    setFilters((prev) => ({
                      ...prev,
                      month,
                      dateFrom: range.dateFrom,
                      dateTo: range.dateTo,
                    }));
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.from', 'Du')}</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, month: '', dateFrom: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('common.to', 'Au')}</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, month: '', dateTo: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label>{t('reglement.search', 'Recherche')}</Label>
                <Input
                  type="text"
                  value={filters.query}
                  onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
                  placeholder={t('common.search', 'Rechercher')}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <div className="d-flex gap-2">
                <Button color="primary" onClick={fetchLedger}>
                  <FaSearch/>
                </Button>
                <Button color="success" onClick={handleExportExcel} disabled={!rows.length} title="Exporter Excel">
                  <FaFileExcel />
                </Button>
                <Button color="danger" onClick={handleExportPdf} disabled={!rows.length} title="Exporter PDF">
                  <FaFilePdf />
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t('common.loading')}</div>
            </div>
          ) : error ? (
            <Alert color="danger">{error}</Alert>
          ) : (
            <div className="d-flex flex-column gap-4">
              {monthlySections.length === 0 ? (
                <div className="text-center text-muted py-4">
                  {t('common.noData', 'Aucune donnée')}
                </div>
              ) : monthlySections.map((section) => (
                <Card key={section.monthKey} className="border-0 shadow-sm bg-light bg-opacity-25">
                  <CardBody>
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1 text-capitalize">{section.monthLabel}</h5>
                        <small className="text-muted">Presentation type releve bancaire</small>
                      </div>
                      <div className="d-flex flex-wrap gap-3">
                        <div className="text-end">
                          <div className="small text-muted">Solde debut</div>
                          <div className="fw-bold">{formatPrice(section.openingBalance, false)}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Debit du mois</div>
                          <div className="fw-bold text-danger">{formatPrice(section.totalDebit, false)}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Credit du mois</div>
                          <div className="fw-bold text-success">{formatPrice(section.totalCredit, false)}</div>
                        </div>
                        <div className="text-end">
                          <div className="small text-muted">Solde fin</div>
                          <div className="fw-bold text-primary">{formatPrice(section.closingBalance, false)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <Table striped hover bordered className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center">{t('common.date', 'Date')}</th>
                            <th className="text-center">{t('common.type', 'Type')}</th>
                            <th className="text-center">{t('common.reference', 'Reference')}</th>
                            <th>{t('common.description', 'Libelle')}</th>
                            <th className="text-end">{t('common.debit', 'Debit')}</th>
                            <th className="text-end">{t('common.credit', 'Credit')}</th>
                            <th className="text-end">{t('common.balanceAfter', 'Solde')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="table-secondary">
                            <td colSpan="6" className="fw-bold">Solde debut de mois</td>
                            <td className="text-end fw-bold">{formatPrice(section.openingBalance, false)}</td>
                          </tr>
                          {section.rows.map((row) => {
                            const Icon = row.icon;
                            return (
                              <tr key={row.id}>
                                <td className="text-center">{formatDateFr(row.date)}</td>
                                <td className="text-center">
                                  <span className="d-inline-flex align-items-center gap-2">
                                    <Icon />
                                    {typeLabel(row.type)}
                                  </span>
                                </td>
                                <td className="text-center">{row.numero}</td>
                                <td>{row.libelle}</td>
                                <td className="text-end">{row.debit ? formatPrice(row.debit, false) : '-'}</td>
                                <td className="text-end">{row.credit ? formatPrice(row.credit, false) : '-'}</td>
                                <td className="text-end fw-bold">{formatPrice(row.balance, false)}</td>
                              </tr>
                            );
                          })}
                          <tr className="table-light">
                            <td colSpan="4" className="fw-bold">Totaux du mois</td>
                            <td className="text-end fw-bold">{formatPrice(section.totalDebit, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(section.totalCredit, false)}</td>
                            <td></td>
                          </tr>
                          <tr className="table-primary">
                            <td colSpan="6" className="fw-bold">Solde fin de mois</td>
                            <td className="text-end fw-bold">{formatPrice(section.closingBalance, false)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default GrandLivreFournisseurPage;


